// React
import { useRef, useState } from "react";

// Types
import { ISyncedEntity } from "../types";

export type TSyncRowStatus = "pending" | "busy" | "done" | "error";

export interface ISyncProgressRow {
  key: string;
  label: string;
  status: TSyncRowStatus;
  count: number;
}

const ENTITY_KEYS = [
  "vendors",
  "customers",
  "invoices",
  "bills",
  "products",
  "purchase_orders",
];
const STAGGER_MS = 450;

interface IUseSyncProgressProps {
  translate: (text: string, options?: any) => string;
}

export const useSyncProgress = ({ translate }: IUseSyncProgressProps) => {
  const [open, setOpen] = useState(false);
  const [isFirstConnect, setIsFirstConnect] = useState(false);
  const [rows, setRows] = useState<ISyncProgressRow[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const retryRef = useRef<() => void>(() => {});

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  const start = (firstConnect: boolean, onRetry: () => void) => {
    clearTimers();
    retryRef.current = onRetry;
    setIsFirstConnect(firstConnect);
    setIsRunning(true);
    setOpen(true);
    setRows(
      ENTITY_KEYS.map((key, i) => ({
        key,
        label: translate(`synced_entity_${key}`),
        status: i === 0 ? "busy" : "pending",
        count: 0,
      })),
    );

    ENTITY_KEYS.forEach((_, i) => {
      if (i === 0) return;
      const timer = setTimeout(() => {
        setRows((prev) =>
          prev.map((row, k) => (k === i ? { ...row, status: "busy" } : row)),
        );
      }, i * STAGGER_MS);
      timersRef.current.push(timer);
    });
  };

  const onProgress = (event: any) => {
    clearTimers();

    if (event.type === "start") {
      const datasets: string[] = event.datasets || [];
      setRows(
        datasets.map((key, i) => ({
          key,
          label: translate(`synced_entity_${key}`),
          status: i === 0 ? "busy" : "pending",
          count: 0,
        })),
      );
    } else if (event.type === "dataset") {
      setRows((prev) => {
        const newRows = [...prev];
        const rowIndex = newRows.findIndex((r) => r.key === event.name);

        if (rowIndex > -1) {
          newRows[rowIndex] = {
            ...newRows[rowIndex],
            status: event.error ? "error" : "done",
            count: event.fetched || 0,
          };

          const nextPendingIndex = newRows.findIndex(
            (r) => r.status === "pending",
          );
          if (nextPendingIndex > -1) {
            newRows[nextPendingIndex] = {
              ...newRows[nextPendingIndex],
              status: "busy",
            };
          }
        }
        return newRows;
      });
    }
  };

  const resolve = (
    entities: ISyncedEntity[] | null,
    errorKeys: string[] = [],
  ) => {
    clearTimers();
    setIsRunning(false);

    if (!entities) {
      setRows((prev) => prev.map((row) => ({ ...row, status: "error" })));
      return;
    }

    if (entities.length === 0) {
      setRows((prev) => prev.map((row) => ({ ...row, status: "done" })));
      return;
    }

    setRows((prev) => {
      const newRows = [...prev];

      entities.forEach((ent) => {
        const existingIdx = newRows.findIndex((r) => r.key === ent.key);
        if (existingIdx > -1) {
          newRows[existingIdx] = {
            ...newRows[existingIdx],
            status: "done",
            count: ent.count,
            label: ent.label,
          };
        } else {
          newRows.push({
            key: ent.key,
            label: ent.label,
            status: "done",
            count: ent.count,
          });
        }
      });

      return newRows.map((r) =>
        r.status === "error" || errorKeys.includes(r.key)
          ? { ...r, status: "error" }
          : { ...r, status: "done" },
      );
    });
  };

  const retry = () => retryRef.current();

  const close = () => {
    if (isRunning) return;
    clearTimers();
    setOpen(false);
  };

  return {
    open,
    isFirstConnect,
    rows,
    isRunning,
    hasError: rows.some((row) => row.status === "error"),
    start,
    resolve,
    retry,
    close,
    onProgress,
  };
};
